import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';

import { PageContext } from '../components/page';
import PageContentWrapper from '../components/pageContentWrapper';
import {
  TripGroupAndAncestorsView,
  TripGroupViewSettings,
} from '../../types/tripGroup';
import { EditTripGroupForm } from './EditTripGroupForm';
import { getTripGroup, updateTripGroup } from '../../service/tripGroupService';
import { getMessageConfigurations } from '../../service/messageConfigurationService';
import type { MessageConfiguration } from '../../types/messageConfiguration';

export const emptyFieldCharacter = '--';

const EditTripGroupContainer: React.FC = () => {
  const { tripGroupId } = useParams<{ tripGroupId: string }>();
  const { pushAlert } = useContext(PageContext);
  const [loading, setLoading] = useState(true);
  const [
    tripGroupSettings,
    setTripGroupSettings,
  ] = useState<TripGroupAndAncestorsView | null>(null);

  const [messageConfigs, setMessageConfigs] = useState<MessageConfiguration[]>([]);

  const getConfigs = async () => {
    try {
      const response = await getMessageConfigurations();
      setMessageConfigs(response);
    } catch (error) {
      setMessageConfigs([]);
      if (error instanceof Error) {
        pushAlert('danger', `Failed to load message configuration. ${error.message}`);
      } else {
        pushAlert('danger', 'Failed to load message configuration. Please contact support or try again later.');
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          getConfigs(),
          tripGroupId && getTripGroup(tripGroupId).then(result => {
            setTripGroupSettings(result);
          }),
        ]);
      } catch {
        setTripGroupSettings(null);
        pushAlert('danger', 'Error getting trip group.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tripGroupId]);

  const saveSettings = async (newTripSettings: TripGroupViewSettings) => {
    setLoading(true);
    try {
      if (tripGroupId) {
      const result = await updateTripGroup(tripGroupId, newTripSettings);
      setTripGroupSettings(result);
      pushAlert('success', 'Trip group successfully saved');
      }
    } catch (err) {
      if (err instanceof Error) {
        pushAlert('danger', `Failed to save Trip group. ${err.message}`);
      } else {
        pushAlert('danger', 'An unknown error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContentWrapper title="EDIT TRIP GROUP" loading={loading}>
      {tripGroupSettings?.tripGroupView && (
        <EditTripGroupForm
          tripGroup={tripGroupSettings}
          saveSettings={saveSettings}
          messageConfigs={messageConfigs}
        />
      )}
    </PageContentWrapper>
  );
};

export default EditTripGroupContainer;
